/**
 * 工作流相关的 TanStack Query Hooks
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { getPublicWorkflowList, getWorkflowDetail } from '@/services/workflowService'
import type {
  PublicWorkflow,
  WorkflowListParams,
  WorkflowListData,
  WorkflowDetail,
} from '@/types/workflow'

/** 工作流列表查询 Key */
const WORKFLOWS_QUERY_KEY = ['workflows'] as const

/** 工作流详情查询 Key */
const WORKFLOW_DETAIL_QUERY_KEY = ['workflow', 'detail'] as const

/**
 * 工作流列表查询结果类型
 */
export interface WorkflowListResult {
  /** 工作流列表 */
  list: PublicWorkflow[]
  /** 总数 */
  total: number
  /** 当前页 */
  page: number
  /** 每页数量 */
  pageSize: number
  /** 总页数 */
  totalPages: number
}

/**
 * 获取公开工作流列表的 Hook
 * @param params 查询参数（分页）
 * @returns 工作流列表查询结果
 */
export function usePublicWorkflows(
  params?: WorkflowListParams
): UseQueryResult<WorkflowListResult> {
  return useQuery({
    queryKey: [...WORKFLOWS_QUERY_KEY, params] as const,
    queryFn: async (): Promise<WorkflowListResult> => {
      const data: WorkflowListData = await getPublicWorkflowList(params)
      const pageSize = data.page_size !== 0 ? data.page_size : (params?.pageSize ?? 12)
      const totalPages = pageSize > 0 ? Math.ceil(data.total / pageSize) : 1
      return {
        list: data.list,
        total: data.total,
        page: data.page,
        pageSize: pageSize,
        totalPages: totalPages,
      }
    },
  })
}

/**
 * 获取工作流详情的 Hook
 * @param id 工作流 ID
 * @returns 工作流详情查询结果
 */
export function useWorkflowDetail(
  id: string | undefined
): UseQueryResult<WorkflowDetail> {
  return useQuery({
    queryKey: [...WORKFLOW_DETAIL_QUERY_KEY, id] as const,
    queryFn: async (): Promise<WorkflowDetail> => {
      if (id === undefined) {
        throw new Error('工作流 ID 不能为空')
      }
      return await getWorkflowDetail(id)
    },
    enabled: id !== undefined,
    staleTime: 5 * 60 * 1000, // 5 分钟内不重新请求
  })
}

export { WORKFLOWS_QUERY_KEY, WORKFLOW_DETAIL_QUERY_KEY }
